import { DateTime, Duration } from "luxon";
import { SharedApiV2 } from "@capstone/mock-api";
import { DropLoadAvailability, ManagedType } from "src/app/core/constants";
import { Site } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import {
  CarrierReference,
  deserializeSimpleCarrier,
  SimpleCarrier,
} from "src/app/partner/global/carriers";
import { Door } from "src/app/partner/settings/doors/door.model";
import {
  ReservationCarrier,
  ReservationVendor,
} from "src/app/partner/settings/reservations/base-reservation-v2.model";
import {
  deserializeReservationVendor,
  isVendorInOrders,
  ReservationType,
} from "src/app/partner/settings/reservations/reservation-v2.model";
import {
  getEnumMember,
  isExistent,
  parseDateTime,
  parseDuration,
  throwUnhandledCaseError,
} from "src/utils";
import { AppointmentUpdatePurchaseOrder } from "../appointment-update.model";

interface DeserializeArguments {
  readonly doors: readonly Door[];
  readonly site: Site;
}

/**
 * The reservation embedded in a reserved slot result. This is a narrower
 * projection than the reservations endpoints return — it carries no
 * `autoExtend`, `expiringSoon` or `modifiedDate` — so it is modelled
 * separately rather than reusing `ReservationV2`.
 */
export class SlotReservation {
  private constructor(
    args: Omit<ClassProperties<SlotReservation>, "displayName" | "type">,
  ) {
    this.carriers = args.carriers;
    this.createdDate = args.createdDate;
    this.doors = args.doors;
    this.dropLoadAvailability = args.dropLoadAvailability;
    this.duration = args.duration;
    this.id = args.id;
    this.isActive = args.isActive;
    this.managedType = args.managedType;
    this.maximumCaseCount = args.maximumCaseCount;
    this.maximumPalletCount = args.maximumPalletCount;
    this.minimumCaseCount = args.minimumCaseCount;
    this.minimumPalletCount = args.minimumPalletCount;
    this.name = args.name;
    this.site = args.site;
    this.startTime = args.startTime;
    this.timeoutOccurrence = args.timeoutOccurrence;
    this.vendors = args.vendors;

    if (this.vendors.length > 0) {
      this.type = ReservationType.Vendor;
      this.displayName =
        this.name || this.vendors.map((vendor) => vendor.name).join(", ");
    } else if (this.carriers.length > 0) {
      this.type = ReservationType.Carrier;
      this.displayName =
        this.name || this.carriers.map((carrier) => carrier.name).join(", ");
    } else {
      throw new Error("No carrier or vendor set on reservation.");
    }
  }

  public readonly carriers: readonly ReservationCarrier[];
  public readonly createdDate: DateTime | null;
  public readonly doors: readonly Door[];
  public readonly dropLoadAvailability: DropLoadAvailability;
  public readonly duration: Duration;
  public readonly id: number;
  public readonly isActive: boolean;
  public readonly managedType: ManagedType | null;
  public readonly maximumCaseCount: number | null;
  public readonly maximumPalletCount: number | null;
  public readonly minimumCaseCount: number | null;
  public readonly minimumPalletCount: number | null;
  public readonly name: string | null;
  public readonly site: Site;
  public readonly startTime: TimeOfDay;
  public readonly timeoutOccurrence: Duration | null;
  public readonly vendors: readonly ReservationVendor[];

  public readonly displayName: string;
  public readonly type: ReservationType;

  public static deserialize(
    data: SharedApiV2.SlotReservationDetailDto,
    { doors, site }: DeserializeArguments,
  ): SlotReservation {
    if (!data.startTime) {
      throw new Error(`Missing start time on reservation "${data.id}".`);
    }

    if (!data.dropLoad) {
      throw new Error(
        `Missing drop load availability on reservation "${data.id}".`,
      );
    }

    // As per business, reservation will only contain one door.
    const apiDoor = data.doors[0];
    const selectedDoor = doors.find((door) => door.id === apiDoor?.id);
    if (!selectedDoor) {
      throw new Error(
        `Could not find reservation door with ID "${apiDoor?.id}".`,
      );
    }

    return new SlotReservation({
      carriers: data.carriers.map((carrier) =>
        deserializeSimpleCarrier({
          id: carrier.carrierId,
          name: carrier.carrierName,
        }),
      ),
      createdDate: data.createdDate
        ? parseDateTime(data.createdDate, site)
        : null,
      doors: [selectedDoor],
      dropLoadAvailability: getEnumMember(DropLoadAvailability, data.dropLoad),
      duration: parseDuration(data.length, "minutes"),
      id: data.id,
      isActive: data.active,
      managedType: data.managedType
        ? getEnumMember(ManagedType, data.managedType)
        : null,
      maximumCaseCount: data.maxCases,
      maximumPalletCount: data.maxPallets,
      minimumCaseCount: data.minCases,
      minimumPalletCount: data.minPallets,
      name: data.name,
      site,
      startTime: TimeOfDay.deserialize(data.startTime),
      timeoutOccurrence: isExistent(data.timeout)
        ? parseDuration(data.timeout, "hours")
        : null,
      vendors: data.vendors.map((vendor) =>
        deserializeReservationVendor(
          {
            id: vendor.id,
            name: vendor.name,
            vendorNumber: vendor.vendorNumber,
          },
          { site },
        ),
      ),
    });
  }

  public isFor({
    carrierOfRecord,
    deliveryCarrier,
    orders,
  }: {
    readonly carrierOfRecord: CarrierReference;
    readonly deliveryCarrier?: SimpleCarrier | null;
    readonly orders: readonly AppointmentUpdatePurchaseOrder[];
  }): boolean {
    switch (this.type) {
      case ReservationType.Carrier: {
        return this.carriers.some(
          (carrier) =>
            carrier.id === carrierOfRecord.id ||
            carrier.id === deliveryCarrier?.id,
        );
      }
      case ReservationType.Vendor: {
        const hasMatchingCarrier = this.carriers.some(
          (carrier) =>
            carrier.id === carrierOfRecord.id ||
            carrier.id === deliveryCarrier?.id,
        );
        const hasVendorInOrders = this.vendors.some((vendor) =>
          isVendorInOrders(vendor, orders),
        );
        return (
          (this.carriers.length === 0 || hasMatchingCarrier) &&
          hasVendorInOrders
        );
      }
      default: {
        throwUnhandledCaseError("reservation type", this.type);
      }
    }
  }

  public isCompatibleWith({
    isDropLoad,
    managedType,
  }: {
    readonly isDropLoad?: boolean;
    readonly managedType?: ManagedType;
  }): boolean {
    return (
      (isDropLoad === undefined || this.isCompatibleWithDropLoad(isDropLoad)) &&
      (managedType === undefined ||
        this.isCompatibleWithManagedType(managedType))
    );
  }

  public isCompatibleWithDropLoad(isDropLoad: boolean): boolean {
    switch (this.dropLoadAvailability) {
      case DropLoadAvailability.Optional:
        return true;
      case DropLoadAvailability.Mandatory:
        return isDropLoad;
      case DropLoadAvailability.NotAllowed:
        return !isDropLoad;
      default:
        throwUnhandledCaseError(
          "drop load availability",
          this.dropLoadAvailability,
        );
    }
  }

  public isCompatibleWithManagedType(managedType: ManagedType): boolean {
    return this.managedType === null || managedType === this.managedType;
  }
}
